import { useState, useCallback, useRef } from "react"
import {
  fetchMembers,
  updateMemberRole,
  fetchRoles,
  fetchRole,
  updateRolePermissions,
} from "../api/membersApi"

function useMutation(fn) {
  const [status, setStatus] = useState("idle")
  const [error, setError] = useState(null)

  const mutate = useCallback(async (...args) => {
    setStatus("pending")
    setError(null)
    try {
      const result = await fn(...args)
      setStatus("success")
      return result
    } catch (err) {
      const msg = err.response?.data?.errors || [err.message || "Something went wrong"]
      setError(msg)
      setStatus("error")
      throw msg
    }
  }, [])

  return { mutate, status, error, isPending: status === "pending", reset: () => { setStatus("idle"); setError(null) } }
}

// ── Members list ─────────────────────────────────────────────────────────────
export function useMembers() {
  const [data, setData] = useState(null) // { memberships, meta }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const result = await fetchMembers(filters)
      setData(result)
    } catch (err) {
      if (err.name !== "AbortError") setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, error, fetch }
}

export const useUpdateMemberRole = () => useMutation((id, slug) => updateMemberRole(id, slug))

// ── Roles ────────────────────────────────────────────────────────────────────
export function useRoles() {
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(false)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchRoles()
      setRoles(result.roles || [])
    } finally {
      setLoading(false)
    }
  }, [])

  return { roles, loading, fetch }
}

export function useRole() {
  const [role, setRole] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetch = useCallback(async (id) => {
    setLoading(true)
    try {
      const result = await fetchRole(id)
      setRole(result.role)
      return result.role
    } finally {
      setLoading(false)
    }
  }, [])

  return { role, loading, fetch, setRole }
}

export const useUpdateRolePermissions = () =>
  useMutation((id, permissions) => updateRolePermissions(id, permissions))
